import api from 'api/client';
import { z } from 'zod';
import { dateSchema, urlSchema } from './shared/common.schema';
import { therapyCourseSchema } from './therapy';

const userEndpoint = `user/me`;
const userByIdEndpoint = `user`;

export enum Gender {
  Female = 'Female',
  Male = 'Male',
  NonBinary = 'Non-Binary',
  PreferNotToDisclose = 'Prefer not to disclose',
}

export enum PaymentMethod {
  AppStore = 'AppStore',
  Stripe = 'Stripe',
}

export enum OnboardingStatus {
  COMPANY_CODE = 'company_code',
  CHALLENGE = 'challenge',
  COACH_SELECTION = 'coach_selection',
  COMPLETED = 'completed',
}

export enum SubscriptionStatus {
  FREE = 'free',
  PREMIUM = 'premium',
  CANCELED = 'canceled',
  PAUSED = 'paused',
  TRIAL = 'trial',
}

export enum Role {
  DEV = 'developer',
  ADMIN = 'admin',
  COACH = 'coach',
  COACH_MANAGER = 'coach_manager',
  THERAPIST = 'therapist',
  PRODUCT = 'product',
  USER = 'user',
  TRUCARE = 'trucare',
  AI_COACH = 'ai_coach',
}

// IMPORTANT!!!
// do not edit manually, copy/paste from the BE: src/types/Permission
export enum Permission {
  // Developer permissions
  DEV_ALL = 'developer:all',

  // Coaching permissions
  // - view own users
  // - view user actions
  // - read chat messages
  COACHING_READ = 'coaching:read',
  // - create and change action items
  // - write chat messages
  // - upload meeting transcript
  // - toggle show/no-show for a meeting
  COACHING_WRITE = 'coaching:write',

  // Moderation permissions
  // - view all users
  // - view user action triggers
  MODERATION_READ = 'moderation:read',
  // - set manual trial on a user
  // - update Coach and Therapist profiles
  // - toggle rotation on a Coach
  // - switch Coach for a user
  // - suspend a user
  // - delete a user
  // - create, update and delete user action triggers
  MODERATION_WRITE = 'moderation:write',

  // Content permissions
  // - view scheduled notifications
  // - view all company codes
  CONTENT_READ = 'content:read',
  // - manage cbt programs
  // - manage notifications
  // - manage therapy courses
  // - manage news
  CONTENT_WRITE = 'content:write',

  // Therapy permissions
  THERAPY_ALL = 'therapy:all',

  // Administration permissions
  // - view all users
  // - switch roles
  ADMINISTRATION_ALL = 'administration:all',
  ADMINISTRATION_IMPERSONATION = 'administration:impersonation',

  // TRUCARE to access our report files
  TRUCARE_FILES_ALL = 'trucare:all',

  // Send mail to a user
  EMAIL_ASSESSMENT = 'email:assessment',

  // AI Assistant permissions
  AI_ASSISTANT_USE = 'ai_assistant:use',
}

// Normalizes casing/variants to Gender; null/empty/invalid -> PreferNotToDisclose. Use for validation so API crap doesn't fail.
const genderSchema = z.preprocess(
  (val) => val ?? null,
  z.union([z.string(), z.null()]).transform((val) => normalizeGender(val))
);

/** Normalize raw API gender (any casing/variants) to Gender enum; null/empty/invalid -> PreferNotToDisclose. Case-insensitive. */
export function normalizeGender(key?: string | null): Gender {
  if (!key) {
    return Gender.PreferNotToDisclose;
  }

  switch (key?.trim().toLowerCase().replace(/\s+/g, ' ')) {
    case 'female':
      return Gender.Female;
    case 'male':
      return Gender.Male;
    case 'non-binary':
    case 'nonbinary':
    case 'non binary':
      return Gender.NonBinary;
    default:
      return Gender.PreferNotToDisclose;
  }
}

export const roleSchema = z.nativeEnum(Role);
export const permissionSchema = z.nativeEnum(Permission);

export const paymentMethodSchema = z.nativeEnum(PaymentMethod);
export const subscriptionStatusSchema = z.nativeEnum(SubscriptionStatus);

const licensePayerSchema = z.object({
  payerId: z.string().min(1, 'Health plan is required'),
  state: z.string().min(1),
});

const licenseSchema = z.object({
  description: z.string(),
  state: z.string(),
  licensePayers: z.array(licensePayerSchema).optional(),
});

const npiSchema = z.preprocess(
  (val) => {
    if (typeof val !== 'string') return val;
    const trimmed = val.trim();
    return trimmed === '' ? null : trimmed;
  },
  z
    .string()
    .regex(/^\d{10}$/, 'NPI must be exactly 10 digits')
    .nullable()
    .optional()
);

const coachSchema = z.object({
  npi: npiSchema,
  tags: z.string().optional(),
  introCallUrl: z.string().optional(),
  weekendWelcomeVideoUrl: z.string().optional(),
  isProd: z.boolean().optional(),
  richTextDescription: z.string().optional(),
  oneLiner: z.string().optional(),
  calendarUrl: z.string().optional(),
  license: z.array(licenseSchema).optional(),
});

export const followupTypeSchema = z.enum([
  'trial',
  'action',
  'meeting',
  'followup',
  'disengaged',
]);

export const followupBucketSchema = z.object({
  isFollowupRequired: z.boolean().optional(),
  isHavingAction: z.boolean().optional(),
  isMeetingCoachToday: z.boolean().optional(),
  isAwaitingResponse: z.boolean().optional(),
  isDisengaged: z.boolean().optional(),
});

export const userRoleSchema = z.object({
  role: roleSchema.or(z.any()).optional(),
});

// got to duplicate these here because of circular dependency
enum TherapySessionStatus {
  SCHEDULED = 'SCHEDULED',
  CANCELED = 'CANCELED',
  CONFIRMED = 'CONFIRMED',
}

const therapySchema = z.object({
  id: z.string(),
  userId: z.string(),
  courseId: z.string(),
  therapistId: z.string(),
  channelId: z.string().optional(),
  status: z.string(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
  assessmentSlug: z.string().or(z.null()).optional(),
  course: therapyCourseSchema.optional(),
});

const therapySessionSchema = z.object({
  id: z.string(),
  therapyId: z.string(),
  therapistId: z.string(),
  meetingId: z.string(),
  userId: z.string(),
  status: z.nativeEnum(TherapySessionStatus),
  copayAmount: z.number(),
});

export const therapyClientSchema = z.object({
  userId: z.string(),
  therapistId: z.string(),
  lastTherapySessionDate: dateSchema,
  therapyName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  hasSuicidalAlert: z.boolean().optional(),
});

export const addressSchema = z.object({
  zipCode: z.string().optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string(),
});

export const insuranceSchema = z.object({
  memberId: z.string().optional(),
  payerId: z.string().optional(),
  planName: z.string().optional(),
  copay: z.number(),
  notes: z.string().optional(),
});

// The form saves insurance only when a payer is picked, so nothing else may be
// filled in without one — it would be dropped on save.
const profileInsuranceSchema = insuranceSchema.refine(
  ({ payerId, memberId, planName, notes, copay }) =>
    !!payerId || (!memberId && !planName && !notes && !copay),
  { message: 'Pick a payer to save insurance details', path: ['payerId'] }
);

export const userSchema = z
  .object({
    nickname: z.string().optional(),
    firstName: z.string().or(z.null()).optional(),
    lastName: z.string().or(z.null()).optional(),
    email: z.string(),
    userId: z.string(),
    isCoach: z.boolean(),
    channelId: z.string().or(z.null()).optional(),
    coachId: z.string().or(z.null()).optional(),
    avatarUrl: z.string().or(z.null()).optional(),
    description: z.string().optional(),
    subscriptionStatus: subscriptionStatusSchema,
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    subscriptionLastPaymentDate: z.string().or(z.null()).optional(),
    isDeleted: z.boolean().optional(),
    trialEndDate: z.string().or(z.null()).optional(),
    notificationsTurnedOn: z.boolean().optional(), // @deprecated
    isNotificationsAllowed: z.boolean().optional(),
    premiumSinceDate: z.string().or(z.null()).optional(),
    canceledSinceDate: z.string().or(z.null()).optional(),
    appVersion: z.string().or(z.null()).optional(),
    timezone: z.string().or(z.null()).optional(),
    coach: coachSchema.optional(),
    phone: z.string().or(z.null()).optional(),
    subscriptionPlan: z
      .enum(['BASIC', 'PLUS', 'PREMIUM', 'BENEFIT'])
      .or(z.null())
      .optional(),
  })
  .merge(coachSchema);

export const userViewSchema = z
  .object({
    gender: genderSchema.nullish(),
    dateOfBirth: dateSchema,
    lastSeenDate: dateSchema,
    lastCallDate: dateSchema,
    firstCallDate: dateSchema,
    activitiesAmount: z.number().optional(),
    callsScheduledAmount: z.number().optional(),
    appVersion: z.string().or(z.null()).optional(),
    platform: z.string().or(z.null()).optional(),
    device: z.string().nullable().optional(),
    ipAddress: z.string().nullable().optional(),
    paymentMethod: paymentMethodSchema.nullable().optional(),
    isAlpha: z.boolean().optional(),
    isBillingIssue: z.boolean().optional(),
    clientCompany: z.string().nullable().optional(),
    followupBucket: followupBucketSchema.or(z.null()).optional(),
    userRole: userRoleSchema.or(z.null()).optional(),
    role: roleSchema.optional(),
    permissions: z.array(permissionSchema).optional(),
    therapies: z.array(therapySchema).optional(),
    therapySessions: z.array(therapySessionSchema).optional(),
    therapistScheduleUrl: z.string().optional(),
    coachScheduleUrl: z.string().optional(),
    onboardingStatus: z.nativeEnum(OnboardingStatus).nullable().optional(),
    preferredService: z.string().nullable().optional(),

    address: addressSchema.optional(),
    insurance: insuranceSchema.optional(),
  })
  .merge(userSchema);

export type License = z.infer<typeof licenseSchema>;
export type LicensePayer = z.infer<typeof licensePayerSchema>;
export type CoachData = z.infer<typeof coachSchema>;
export type User = z.infer<typeof userSchema>;
export type UserView = z.infer<typeof userViewSchema>;
export type UserData = {
  me: UserView;
  coach?: UserView;
};

type Answer = {
  key?: string;
  value?: string | number | boolean | Date;
  text?: string;
};

type UserAnswer = {
  questionSlug: string;
  answers: Answer[];
};

export const getUserData = async () => {
  return api(userEndpoint).json<UserData>();
};

// This schema is intentionally scoped to fields we edit in the profile UI.
export const profileFormSchema = z.object({
  nickname: z.string().optional(),
  firstName: z.string().min(1, 'First name is required').optional(),
  lastName: z.string().min(1, 'Last name is required').optional(),
  avatarUrl: urlSchema,
  calendarUrl: urlSchema,
  npi: npiSchema,
  gender: genderSchema,
  dateOfBirth: dateSchema,
  phone: z.string().optional(),
  description: z.string().optional(),
  introCallUrl: urlSchema,
  oneLiner: z.string().optional(),
  richTextDescription: z.string().optional(),
  tags: z.string().optional(),
  timezone: z.string().optional(),
  weekendWelcomeVideoUrl: urlSchema,
  license: z.array(licenseSchema).optional(),
  address: addressSchema.optional(),
  insurance: profileInsuranceSchema.optional(),
});

export type UpdateUserFormValues = z.infer<typeof profileFormSchema>;

export const updateUser = async (
  body: UpdateUserFormValues,
  userId?: string
) => {
  const endpoint = userId ? `user/${userId}` : userEndpoint;
  return api.put(endpoint, { json: body, timeout: 60 * 1000 }).json<UserData>();
};

export type GetUserByIdResponse = {
  user: UserData;
  onboarding?: UserAnswer[];
};
export const getUserById = async (userId: string) => {
  return api(`${userByIdEndpoint}/v2/${userId}`).json<GetUserByIdResponse>();
};

export const toggleRotation = async (userId: string) => {
  return api.put(`user/${userId}/toggle-rotation`).json<User>();
};

export const toggleFollowRequirement = async (userId: string) =>
  api.put(`user/${userId}/toggle-followup-requirement`).json<User>();
